import { Link, useNavigate } from 'react-router-dom';
import { FaSignInAlt, FaUser } from 'react-icons/fa';
import useAuthHook from '../../api/auth';
import useAuth from '../../hooks/useAuth';
import LanguageSwitcher from "./LanguageSwitcher";
import useUserCategoriesHook from '../../api/panel/userCategories';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

const Navigation = () => {
    const navigate = useNavigate();
    const { postLogout } = useAuthHook();
    const { auth, setAuth } = useAuth();
    const {t} = useTranslation();

    // Use the user categories hook to fetch categories
    const { getGuestCategories, getAdminCategories } = useUserCategoriesHook({});

    // Fetch appropriate categories based on auth state
    const maybeQuery = auth?.userInfo ? getAdminCategories : getGuestCategories;
    const query = typeof maybeQuery === 'function' ? maybeQuery() : maybeQuery;

    const categoriesData = query?.data;
    const isLoading = query?.isLoading ?? false;
    const isError = query?.isError ?? false;



    const { mutateAsync: logout } = useMutation(postLogout, {
        onSuccess: () => {
            setAuth(null);
            navigate('/admin/login');
        },
    });

    const handleLogout = () => {
        logout({});
    };

    const user = () => {
        return auth?.userInfo;
    };

    const organizeMenus = () => {
        const categories = Array.isArray(categoriesData?.categories) ? categoriesData.categories : [];

        // Safely map categories, ensuring each item has a menu property
        const dropdownItems = categories.map((c) => c?.menu).filter((m) => typeof m === 'string');
        const uniqueDropdowns = [...new Set(dropdownItems.filter(m => m && m !== 'hidden' && m !== 'normal'))];

        // Separate profile item
        const profileDropdown = uniqueDropdowns.includes('account') ? 'account' : null;
        const otherDropdowns = uniqueDropdowns.filter(m => m !== 'account');

        // Organize other dropdowns and append profile at the end
        const sortedDropdowns = profileDropdown ? [...otherDropdowns, profileDropdown] : otherDropdowns;

        return { sortedDropdowns, menuItems: categories };
    };

    const renderAuthItems = () => {
        const { sortedDropdowns, menuItems } = organizeMenus();

        return (
            <ul className='navbar-nav ms-auto'>
                {menuItems.map((menu, index) => {
                    if (menu.menu === 'normal' && menu.auth === true) {
                        return (
                            <li key={menu._id || `menu-item-${index}`} className='nav-item'>
                                <Link to={menu.path} className='nav-link' aria-current='page'>
                                    {t(menu.name)}
                                </Link>
                            </li>
                        );
                    } else {
                        return null;
                    }
                })}

                {sortedDropdowns.map((item, index) => (
                    item && (
                        <li key={`${item}-${index}`} className='nav-item dropdown'>
                            <a
                                className='nav-link dropdown-toggle'
                                id='navbarDropdownMenuLink'
                                role='button'
                                data-bs-toggle='dropdown'
                                aria-expanded='false'
                            >
                                {item === 'account' ? (
                                    <>
                                        <FaUser className='me-1 menu-navigation-icon navigation-profile-icon' />
                                        {user()?.name || t('Account')}
                                    </>
                                ) : (
                                    t(typeof item === 'string' ? item.charAt(0).toUpperCase() + item.substring(1) : '')
                                )}
                            </a>
                            <ul className='dropdown-menu border-0' aria-labelledby='navbarDropdownMenuLink'>
                                {menuItems.map((menu) => {
                                    if (menu.menu === item) {
                                        return (
                                            <li key={menu._id}>
                                                <Link to={menu.path} className='dropdown-item'>
                                                    {t(menu.name)}
                                                </Link>
                                            </li>
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                                {item === 'account' && (
                                    <li key={`profile-logout-${index}`}>
                                        <Link
                                            to='/admin/login'
                                            className='dropdown-item logout-menu-item'
                                            onClick={handleLogout}
                                        >
                                            {t("Logout")}
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </li>
                    )
                ))}
            </ul>
        );
    };

    const renderGuestItems = () => (
        <ul className='navbar-nav ms-auto'>
            <li key='guest-item-about' className='nav-item'>
                <Link to='/about' className='nav-link' aria-current='page'>{t("About")}</Link>
            </li>
            <li key='guest-item-events' className='nav-item'>
                <Link to='/events' className='nav-link' aria-current='page'>{t("Events")}</Link>
            </li>
            <li key='guest-item-spaces' className='nav-item'>
                <Link to='/spaces' className='nav-link' aria-current='page'>{t("Spaces")}</Link>
            </li>
            <li key='guest-item-spaces' className='nav-item'>
                <Link to='/open-calls' className='nav-link' aria-current='page'>{t("Open Calls")}</Link>
            </li>
            <li key='guest-item-login' className='nav-item make-some-space'>
                <Link to='/admin/login' className='nav-link' aria-current='page'>
                    <FaSignInAlt className='mb-1'/> {t("Login")}
                </Link>
            </li>
        </ul>
    );

    if (isLoading) {
        return (
            <nav className='navbar navbar-expand-md navbar-light bg-light'>
                <div className='container'>
                    <Link to='/'>
                        <img
                            width='80'
                            height='80'
                            src='/img/artnet_logo_transparent_black.png'
                            className='img-fluid brand-logos'
                            alt='logo'
                        />
                    </Link>
                    <div className='collapse navbar-collapse show' id='navbarNav'>
                        {renderGuestItems()}
                    </div>
                    <LanguageSwitcher />
                </div>
            </nav>
        );
    }


    if (isError) {
        return (
            <nav className='navbar navbar-expand-md navbar-light bg-light'>
                <div className='container'>
                    <Link to='/'>
                        <img
                            width='80'
                            height='80'
                            src='/img/artnet_logo_transparent_black.png'
                            className='img-fluid brand-logos'
                            alt='logo'
                        />
                    </Link>
                    <div className='collapse navbar-collapse show' id='navbarNav'>
                        {renderGuestItems()}
                    </div>
                    <LanguageSwitcher />
                </div>
            </nav>
        );
    }


    return (
        <nav className='navbar navbar-expand-md navbar-light bg-light'>
            <div className='container'>
                <Link to='/'>
                    <img
                        width='80'
                        height='80'
                        src='/img/artnet_logo_transparent_black.png'
                        className='img-fluid brand-logos'
                        alt='logo'
                    />
                </Link>
                <button
                    className='navbar-toggler'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target='#navbarNav'
                    aria-controls='navbarNav'
                    aria-expanded='false'
                    aria-label='Toggle navigation'
                >
                    <span className='navbar-toggler-icon'></span>
                </button>
                <div className='collapse navbar-collapse' id='navbarNav'>
                    {auth?.userInfo ? renderAuthItems() : renderGuestItems()}
                </div>
                <LanguageSwitcher />
            </div>
        </nav>
    );
};

export default Navigation;
